import { useFilters } from "@/context/FilterContext";
import { useApp } from "@/context/AppContext";
import { Badge, completionVariant, completionLabel } from "./Badge";
import type { Requirement, Test } from "@/types";

interface DataTableProps {
  items: (Requirement | Test)[];
  view: "requirements" | "tests";
}

const REQ_COLUMNS = [
  { key: "id", label: "ID" },
  { key: "title", label: "Title" },
  { key: "description", label: "Description" },
  { key: "level", label: "Level" },
  { key: "discipline", label: "Discipline" },
  { key: "completion", label: "Status" },
  { key: "tags", label: "Tags" },
];

const TEST_COLUMNS = [
  { key: "id", label: "ID" },
  { key: "title", label: "Title" },
  { key: "description", label: "Description" },
  { key: "type", label: "Type" },
  { key: "suite", label: "Suite" },
  { key: "jira_status", label: "Status" },
];

export function DataTable({ items, view }: DataTableProps) {
  const { sortField, sortDir, setSort } = useFilters();
  const { navigateTo, activeId } = useApp();
  const columns = view === "tests" ? TEST_COLUMNS : REQ_COLUMNS;

  const sorted = [...items].sort((a, b) => {
    const av = (a as unknown as Record<string, unknown>)[sortField];
    const bv = (b as unknown as Record<string, unknown>)[sortField];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
    return sortDir === "desc" ? -cmp : cmp;
  });

  function handleSort(key: string) {
    if (sortField === key) {
      setSort(key, sortDir === "asc" ? "desc" : "asc");
    } else {
      setSort(key, "asc");
    }
  }

  function handleRowClick(id: string) {
    navigateTo(view, id);
  }

  return (
    <div className="overflow-auto px-7">
      <table className="w-full border-collapse text-xs" data-testid="data-table">
        <thead>
          <tr>
            {columns.map(col => (
              <th
                key={col.key}
                className={`px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide border-b border-border cursor-pointer select-none whitespace-nowrap hover:text-text ${sortField === col.key ? "text-accent" : "text-text2"}`}
                onClick={() => handleSort(col.key)}
              >
                {col.label}
                {sortField === col.key && (
                  <span className="ml-1">{sortDir === "asc" ? "↑" : "↓"}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(item => (
            <tr
              key={item.id}
              onClick={() => handleRowClick(item.id)}
              className={`border-b border-border/50 cursor-pointer transition-colors duration-100 ${item.id === activeId ? "bg-accent/[0.08] hover:bg-accent/[0.12]" : "hover:bg-bg3"}`}
              data-testid={`row-${item.id}`}
            >
              {view === "requirements" ? (
                <RequirementRow item={item as Requirement} />
              ) : (
                <TestRow item={item as Test} />
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {items.length === 0 && (
        <div className="text-center p-8 text-text2">No items match the current filters</div>
      )}
    </div>
  );
}

function RequirementRow({ item }: { item: Requirement }) {
  return (
    <>
      <td className="px-3 py-2 text-text align-middle"><span className="text-accent font-semibold text-xs">{item.id}</span></td>
      <td className="px-3 py-2 text-text align-middle"><span className="text-text2">{item.title || "—"}</span></td>
      <td className="px-3 py-2 text-text align-middle"><span className="text-text2">{item.description || "—"}</span></td>
      <td className="px-3 py-2 text-text align-middle"><Badge>{item.level}</Badge></td>
      <td className="px-3 py-2 text-text align-middle">{item.discipline && <Badge variant="info">{item.discipline}</Badge>}</td>
      <td className="px-3 py-2 text-text align-middle"><Badge variant={completionVariant(item.completion)}>{completionLabel(item.completion)}</Badge></td>
      <td className="px-3 py-2 text-text align-middle">
        {item.tags && item.tags.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            {item.tags.map(t => <Badge key={t} variant="muted">{t}</Badge>)}
          </div>
        )}
      </td>
    </>
  );
}

function TestRow({ item }: { item: Test }) {
  return (
    <>
      <td className="px-3 py-2 text-text align-middle"><span className="text-accent font-semibold text-xs">{item.id}</span></td>
      <td className="px-3 py-2 text-text align-middle"><span className="text-text2">{item.title || "—"}</span></td>
      <td className="px-3 py-2 text-text align-middle"><span className="text-text2">{item.description}</span></td>
      <td className="px-3 py-2 text-text align-middle"><Badge>{item.type}</Badge></td>
      <td className="px-3 py-2 text-text align-middle"><Badge variant="info">{item.suite}</Badge></td>
      <td className="px-3 py-2 text-text align-middle">
        {item.jira_status && (
          <Badge variant={item.all_done ? "success" : item.jira_status === "missing link" ? "muted" : "warning"}>
            {item.jira_status}
          </Badge>
        )}
      </td>
    </>
  );
}
